import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useMotionValueEvent,
} from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useSEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/AnimatedSection";
import { useHeaderHidden } from "../components/useHeaderHidden";
import { services } from "../data/services";

const EASE = [0.22, 1, 0.36, 1];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
  else el.scrollIntoView({ behavior: "smooth" });
};

/* ---------------- Visual 1: browser assembling ---------------- */

const BrowserVisual = () => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-20%" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14 } } }}
    className="overflow-hidden rounded-3xl border border-[#07130C]/10 bg-white shadow-[0_30px_80px_rgba(14,58,40,0.14)]"
    data-testid="visual-browser"
  >
    <motion.div
      variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } } }}
      className="flex items-center gap-3 border-b border-[#07130C]/8 px-5 py-3.5"
    >
      <span className="flex gap-1.5">
        <i className="h-2.5 w-2.5 rounded-full bg-[#B8912A]/70" />
        <i className="h-2.5 w-2.5 rounded-full bg-[#D3B74D]/70" />
        <i className="h-2.5 w-2.5 rounded-full bg-[#3CBE6E]/70" />
      </span>
      <span className="flex-1 rounded-full bg-[#07130C]/5 px-4 py-1 text-[11px] text-[#07130C]/45">
        yourbusiness.com — built to convert
      </span>
    </motion.div>
    <div className="space-y-4 p-6">
      {[
        <div key="nav" className="flex items-center justify-between">
          <span className="h-3 w-20 rounded-full bg-[#07130C]/15" />
          <span className="flex gap-2">
            <i className="block h-2.5 w-8 rounded-full bg-[#07130C]/10" />
            <i className="block h-2.5 w-8 rounded-full bg-[#07130C]/10" />
            <i className="block h-2.5 w-14 rounded-full bg-gradient-to-r from-[#1C7543] to-[#D3B74D]" />
          </span>
        </div>,
        <div key="hero" className="rounded-2xl bg-gradient-to-br from-[#07130C] to-[#0E3A28] p-6">
          <span className="block h-4 w-3/4 rounded-full bg-white/25" />
          <span className="mt-3 block h-4 w-1/2 rounded-full bg-white/15" />
          <span className="mt-5 block h-7 w-28 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
        </div>,
        <div key="content" className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((k) => (
            <span key={k} className="h-16 rounded-xl border border-[#07130C]/8 bg-[#F8FBF6]" />
          ))}
        </div>,
        <div key="cta" className="flex items-center justify-between rounded-2xl border border-[#1C7543]/20 bg-[#1C7543]/5 px-5 py-4">
          <span className="h-3 w-32 rounded-full bg-[#1C7543]/30" />
          <span className="h-7 w-24 rounded-full bg-gradient-to-r from-[#1C7543] to-[#D3B74D]" />
        </div>,
        <div key="footer" className="flex justify-between border-t border-[#07130C]/8 pt-4">
          <span className="h-2.5 w-24 rounded-full bg-[#07130C]/10" />
          <span className="h-2.5 w-16 rounded-full bg-[#07130C]/10" />
        </div>,
      ].map((block, i) => (
        <motion.div
          key={i}
          variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } } }}
        >
          {block}
        </motion.div>
      ))}
    </div>
  </motion.div>
);

/* ---------------- Visual 2: layered dashboard parallax ---------------- */

const DashboardVisual = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 8);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}
      className="relative h-[380px] [perspective:1200px]"
      data-testid="visual-dashboard"
    >
      <motion.div style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }} className="relative h-full">
        <Reveal className="absolute inset-x-8 top-0 [transform:translateZ(-60px)]">
          <div className="rounded-3xl border border-[#07130C]/10 bg-white p-6 shadow-xl">
            <div className="flex h-36 items-end gap-3">
              {[40, 65, 50, 80, 58, 92, 70].map((h, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.08, ease: EASE }}
                  className="flex-1 rounded-t-lg bg-gradient-to-t from-[#1C7543]/25 to-[#1C7543]/60"
                />
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2} className="absolute -left-2 bottom-16 w-52 [transform:translateZ(40px)]">
          <div className="rounded-2xl border border-[#07130C]/10 bg-white p-5 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#07130C]/40">Active users</p>
            <p className="mt-1 font-display text-3xl font-semibold text-[#07130C]">1,204</p>
            <p className="mt-1 text-xs font-semibold text-[#1C7543]">Admin panel · live</p>
          </div>
        </Reveal>
        <Reveal delay={0.35} className="absolute -right-2 bottom-4 w-56 [transform:translateZ(80px)]">
          <div className="rounded-2xl bg-gradient-to-br from-[#07130C] to-[#0E3A28] p-5 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Customer portal</p>
            <div className="mt-3 space-y-2">
              {[0, 1, 2].map((k) => (
                <span key={k} className="block h-2.5 rounded-full bg-white/15" style={{ width: `${85 - k * 20}%` }} />
              ))}
            </div>
          </div>
        </Reveal>
      </motion.div>
    </div>
  );
};

/* ---------------- Visual 3: CRM pipeline ---------------- */

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won"];

const PipelineVisual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.35"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  const left = useTransform(smooth, [0, 1], ["1%", "81%"]);
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) =>
    setActive(Math.min(STAGES.length - 1, Math.floor(v * (STAGES.length + 0.3)))),
  );

  return (
    <div ref={ref} className="rounded-3xl border border-[#07130C]/10 bg-white p-6 shadow-[0_30px_80px_rgba(14,58,40,0.14)] md:p-8" data-testid="visual-pipeline">
      <div className="relative">
        <div className="flex justify-between gap-2">
          {STAGES.map((s, i) => (
            <div
              key={s}
              className={`flex-1 rounded-xl border px-2 py-3 text-center text-[10px] font-bold uppercase tracking-wider transition-all duration-500 md:text-xs ${
                i <= active
                  ? "border-transparent bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white"
                  : "border-[#07130C]/10 bg-[#F8FBF6] text-[#07130C]/45"
              }`}
            >
              {s}
            </div>
          ))}
        </div>
        <motion.div
          style={{ left }}
          className="absolute -bottom-24 w-[17%] min-w-[120px] md:-bottom-28"
        >
          <div className="rounded-2xl border border-[#1C7543]/30 bg-white p-4 shadow-[0_18px_50px_rgba(28,117,67,0.3)]">
            <p className="flex items-center gap-2 text-xs font-bold text-[#07130C]">
              <span className="h-2 w-2 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
              New Lead
            </p>
            <p className="mt-1.5 text-[11px] leading-snug text-[#07130C]/50">
              From website form — never falls through the cracks
            </p>
          </div>
        </motion.div>
      </div>
      <div className="h-32 md:h-36" />
      <p className="text-center text-[11px] uppercase tracking-[0.25em] text-[#07130C]/35">
        Scroll — watch a lead move through your pipeline
      </p>
    </div>
  );
};

/* ---------------- Visual 4: AI flow ---------------- */

const AI_STEPS = ["Input", "AI", "Decision", "Automation", "Result"];

const AIFlowVisual = () => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-20%" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.35 } } }}
    className="mx-auto flex max-w-sm flex-col items-center"
    data-testid="visual-ai-flow"
  >
    {AI_STEPS.map((step, i) => (
      <div key={step} className="flex w-full flex-col items-center">
        {i > 0 && (
          <motion.span
            variants={{ hidden: { scaleY: 0 }, show: { scaleY: 1, transition: { duration: 0.4, ease: EASE } } }}
            className="h-10 w-[2px] origin-top bg-gradient-to-b from-[#3CBE6E] to-[#B8912A]"
          />
        )}
        <motion.div
          variants={{
            hidden: { opacity: 0.15, scale: 0.92 },
            show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
          }}
          className={`flex w-full items-center justify-center gap-3 rounded-2xl border px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] ${
            i === 1
              ? "border-transparent bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white shadow-[0_14px_40px_rgba(28,117,67,0.4)]"
              : "border-[#07130C]/10 bg-white text-[#07130C]"
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${i === 1 ? "bg-white" : "bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]"}`} />
          {step}
        </motion.div>
      </div>
    ))}
  </motion.div>
);

/* ---------------- Visual 5: SEO graph ---------------- */

const SEOGraphVisual = () => (
  <div className="rounded-3xl border border-[#07130C]/10 bg-white p-6 shadow-[0_30px_80px_rgba(14,58,40,0.14)] md:p-8" data-testid="visual-seo-graph">
    <svg viewBox="0 0 600 300" className="w-full" role="img" aria-label="Illustrative ranking improvement graph">
      <defs>
        <linearGradient id="seoLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3CBE6E" />
          <stop offset="100%" stopColor="#B8912A" />
        </linearGradient>
        <linearGradient id="seoArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1C7543" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#1C7543" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[60, 120, 180, 240].map((y) => (
        <line key={y} x1="40" y1={y} x2="580" y2={y} stroke="#07130C" strokeOpacity="0.06" />
      ))}
      <motion.path
        d="M 40 235 C 160 235, 200 228, 260 210 C 330 190, 380 130, 450 95 C 500 70, 540 62, 570 55"
        fill="none"
        stroke="url(#seoLine)"
        strokeWidth="3"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, margin: "-20%" }}
        transition={{ duration: 2.2, ease: "easeInOut" }}
      />
      <motion.path
        d="M 40 235 C 160 235, 200 228, 260 210 C 330 190, 380 130, 450 95 C 500 70, 540 62, 570 55 L 570 260 L 40 260 Z"
        fill="url(#seoArea)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-20%" }}
        transition={{ duration: 1, delay: 1.4 }}
      />
      {[
        { x: 40, y: 235, label: "Before", d: 0.1 },
        { x: 260, y: 210, label: "Optimization", d: 0.9 },
        { x: 570, y: 55, label: "Improvement", d: 1.9 },
      ].map((p) => (
        <motion.g
          key={p.label}
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: p.d, duration: 0.4 }}
          style={{ transformOrigin: `${p.x}px ${p.y}px` }}
        >
          <circle cx={p.x} cy={p.y} r="6" fill="#fff" stroke="url(#seoLine)" strokeWidth="3" />
          <text x={Math.min(p.x, 500)} y={p.y - 16} fill="#07130C" fontSize="12" fontWeight="700" fontFamily="inherit">
            {p.label}
          </text>
        </motion.g>
      ))}
    </svg>
    <p className="mt-4 text-center text-[11px] uppercase tracking-[0.25em] text-[#07130C]/35">
      Illustrative trend — not client data
    </p>
  </div>
);

const VISUALS = {
  "website-design": BrowserVisual,
  "saas-development": DashboardVisual,
  "crm-systems": PipelineVisual,
  "ai-automation": AIFlowVisual,
  seo: SEOGraphVisual,
};

/* ---------------- Page ---------------- */

const Services = () => {
  const [activeSection, setActiveSection] = useState(services[0].id);
  const headerHidden = useHeaderHidden();
  useSEO(
    "Services | Social Wavez Technologies",
    "Website Designing & Development, SaaS Development, CRM & Business Systems, AI & Automation, and Website SEO — built individually, or as one integrated system.",
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-35% 0px -55% 0px" },
    );
    services.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="bg-deep relative overflow-hidden pb-20 pt-40 md:pb-28 md:pt-52">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />
        <div className="animate-float-slow pointer-events-none absolute -right-24 top-16 h-80 w-80 rounded-full bg-[#1C7543]/20 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
              What We Build
            </p>
          </Reveal>
          <h1 className="mt-7 max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLines
              trigger="mount"
              delay={0.25}
              lines={[
                "Five Capabilities.",
                <span key="sv2" className="text-gradient-aurora">Every One of Them Connects.</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">
              Website Designing & Development, SaaS Development, CRM & Business
              Systems, AI & Automation, and Website SEO — built individually, or
              as one integrated system.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <button
              type="button"
              onClick={() => scrollToId(services[0].id)}
              data-testid="services-scroll-cue"
              className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-white"
            >
              <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Explore the five
            </button>
          </Reveal>
        </div>
      </section>

      {/* Sticky scrollspy nav — follows the header: sits under it when visible, pins to top when it hides */}
      <div
        className={`sticky z-30 border-b border-[#07130C]/8 bg-white/80 backdrop-blur-xl transition-[top] duration-300 ease-out ${
          headerHidden ? "top-0" : "top-20"
        }`}
      >
        <div className="no-scrollbar mx-auto flex max-w-7xl gap-2 overflow-x-auto px-6 py-3 md:px-12" role="tablist" aria-label="Service sections">
          {services.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`services-nav-${s.id}`}
              aria-selected={activeSection === s.id}
              onClick={(e) => {
                e.preventDefault();
                scrollToId(s.id);
              }}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                activeSection === s.id
                  ? "bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white shadow-[0_6px_20px_rgba(28,117,67,0.35)]"
                  : "text-[#07130C]/55 hover:bg-[#07130C]/5 hover:text-[#07130C]"
              }`}
            >
              {s.short}
            </a>
          ))}
        </div>
      </div>

      {/* Sections */}
      {services.map((s, i) => {
        const Visual = VISUALS[s.id];
        const flip = i % 2 === 1;
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={`service-section-${s.id}`}
            className={`scroll-mt-32 py-24 md:py-32 ${i % 2 === 0 ? "bg-white" : "bg-[#F8FBF6]"}`}
          >
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
              <div className={flip ? "lg:order-2" : ""}>
                <Reveal>
                  <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#07130C]/45">
                    <span className="text-gradient-pulse font-display text-sm font-bold tracking-normal">{s.index}</span>
                    <span className="h-px w-12 bg-[#07130C]/15" />
                    <span>{s.title}</span>
                  </div>
                </Reveal>
                <MaskedLines
                  lines={[s.headline]}
                  className="mt-6 block font-display text-3xl font-semibold leading-[1.12] tracking-tight text-[#07130C] md:text-4xl"
                />
                <Reveal delay={0.2}>
                  <p className="mt-5 text-base leading-relaxed text-[#07130C]/60">{s.sectionCopy}</p>
                </Reveal>
                <Reveal delay={0.3}>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {s.capabilities.map((c) => (
                      <span
                        key={c}
                        className="rounded-full border border-[#07130C]/10 bg-white px-4 py-1.5 text-xs font-medium text-[#07130C]/70 shadow-sm"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </Reveal>
                <Reveal delay={0.4}>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-[#1C7543] transition-colors hover:text-[#D3B74D]"
                  >
                    {s.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              </div>
              <div className={flip ? "lg:order-1" : ""}>
                <Visual />
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
};

export default Services;
